import { PACKAGE_ROLES, PROSPECT_ROLES, ROLES, isKnownRole, roleOf } from "./roles";
import { checkScope, normalizeIdList } from "./scope";
import { GRANTS, PERMISSION_SET } from "./permissions";

export const REASONS = Object.freeze({
  ALLOWED: "ALLOWED",
  UNAUTHENTICATED: "UNAUTHENTICATED",
  ACCESS_NOT_READY: "ACCESS_NOT_READY",
  UNKNOWN_ROLE: "UNKNOWN_ROLE",
  UNKNOWN_PERMISSION: "UNKNOWN_PERMISSION",
  ROLE_NOT_ALLOWED: "ROLE_NOT_ALLOWED",
  CONDITION_FAILED: "CONDITION_FAILED",
  MISSING_RESOURCE: "MISSING_RESOURCE",
  RESOURCE_NOT_ASSIGNED: "RESOURCE_NOT_ASSIGNED",
  PACKAGE_RESTRICTED: "PACKAGE_RESTRICTED",
  SUBSCRIPTION_REQUIRED: "SUBSCRIPTION_REQUIRED",
  BRANCH_ASSIGNMENT_REQUIRED: "BRANCH_ASSIGNMENT_REQUIRED",
});

export const ACCESS_STATUS = Object.freeze({
  UNAUTHENTICATED: "unauthenticated",
  LOADING: "loading",
  READY: "ready",
});

const deny = (reason, extra) => ({ allowed: false, reason, ...extra });

// Development only: a permission id that is not registered is almost always a
// typo, and it silently denies. Warn once per id (never in production builds
// or tests, where unknown ids are exercised on purpose).
const warnedUnknown = new Set();
const warnUnknownPermission = (permission) => {
  if (process.env.NODE_ENV !== "development") return;
  const key = String(permission);
  if (warnedUnknown.has(key)) return;
  warnedUnknown.add(key);
  // eslint-disable-next-line no-console
  console.warn(`[access] unknown permission "${key}" is denied. Check the id in src/access/modules.`);
};

/**
 * Derives the access status from the existing auth Context values.
 * - no token                       -> unauthenticated
 * - token but no user loaded yet   -> loading (never render protected UI)
 * - token and a user with a role   -> ready
 */
export const resolveStatus = ({ isAuthenticated, user }) => {
  if (!isAuthenticated) return ACCESS_STATUS.UNAUTHENTICATED;
  if (!user || typeof user.role !== "string" || !user.role) {
    return ACCESS_STATUS.LOADING;
  }
  return ACCESS_STATUS.READY;
};

const holds = (rule, role) => rule.roles.includes(role);

const snapshotUser = (user) =>
  Object.freeze({
    role: roleOf(user),
    between: Object.freeze(normalizeIdList(user?.between)),
    assignedBranch: Object.freeze(normalizeIdList(user?.assignedBranch)),
    globalAccess: user?.globalAccess === true,
  });

/**
 * Builds the single authorization evaluator for one identity.
 * Pure and synchronous: no fetching, safe to call on every render.
 */
export const createPolicy = ({ isAuthenticated, user } = {}) => {
  const status = resolveStatus({ isAuthenticated, user });
  const role = status === ACCESS_STATUS.READY ? roleOf(user) : null;
  // Frozen snapshot of the only user fields authorization reads (see
  // policySignature). Later mutation of the caller's user object cannot change
  // a decision, and a cached policy never sees a different user than its key.
  const identity = { role, user: snapshotUser(user) };
  const needsBranchAssignment =
    role === ROLES.BRANCH && identity.user.assignedBranch.length === 0;

  // `ignoreScope` answers "does this ROLE hold the permission at all?" - used
  // to decide what to show. Execution must always use `can(permission, resource)`.
  const evaluate = (permission, resource, options = {}) => {
    if (status === ACCESS_STATUS.UNAUTHENTICATED) {
      return deny(REASONS.UNAUTHENTICATED);
    }
    if (status === ACCESS_STATUS.LOADING) return deny(REASONS.ACCESS_NOT_READY);
    if (!isKnownRole(role)) return deny(REASONS.UNKNOWN_ROLE);
    if (typeof permission !== "string" || !PERMISSION_SET.has(permission)) {
      warnUnknownPermission(permission);
      return deny(REASONS.UNKNOWN_PERMISSION);
    }

    const rule = GRANTS[permission];
    if (!rule || !holds(rule, role)) {
      const base = PACKAGE_ROLES[role];
      const afterActivation = PROSPECT_ROLES[role];
      return deny(
        base && rule && holds(rule, base)
          ? REASONS.PACKAGE_RESTRICTED
          : afterActivation && rule && holds(rule, afterActivation)
            ? REASONS.SUBSCRIPTION_REQUIRED
            : REASONS.ROLE_NOT_ALLOWED,
      );
    }

    // Branch pages need a real assignment (NA-035). Only the `branch` role is
    // held to it: staff with assigned branches keep their own rules, and an
    // empty list is never read as "all branches".
    if (rule.requiresBranch && needsBranchAssignment) {
      return deny(REASONS.BRANCH_ASSIGNMENT_REQUIRED);
    }

    if (rule.scope && !options.ignoreScope) {
      const scope = checkScope(rule.scope, identity, resource);
      if (!scope.ok) return deny(scope.reason, { scope: rule.scope });
    }

    if (rule.when) {
      let ok = false;
      try {
        ok = rule.when({ role, user: identity.user, resource: resource || {} }) === true;
      } catch {
        ok = false;
      }
      if (!ok) return deny(REASONS.CONDITION_FAILED);
    }

    return { allowed: true, reason: REASONS.ALLOWED, scope: rule.scope };
  };

  const can = (permission, resource) => evaluate(permission, resource).allowed;

  // Role-only check for UI visibility of scoped permissions (scope ignored).
  const has = (permission) => evaluate(permission, undefined, { ignoreScope: true }).allowed;

  // Empty (or non-array) input is denied: "any of nothing" and "all of
  // nothing" must never turn into access.
  const canAny = (permissions, resource) =>
    Array.isArray(permissions) &&
    permissions.length > 0 &&
    permissions.some((permission) => can(permission, resource));

  const canAll = (permissions, resource) =>
    Array.isArray(permissions) &&
    permissions.length > 0 &&
    permissions.every((permission) => can(permission, resource));

  return {
    status,
    role,
    isReady: status === ACCESS_STATUS.READY,
    needsBranchAssignment,
    isAuthenticated: status !== ACCESS_STATUS.UNAUTHENTICATED,
    evaluate,
    can,
    has,
    canAny,
    canAll,
  };
};

// Policies are shared across components: one per distinct identity signature.
const cache = new Map();
const CACHE_LIMIT = 20;

// Cache key. It lists exactly the inputs the evaluator reads: authentication,
// the role (status, grants, package restriction) and the normalized `between`
// / `assignedBranch` lists (resource scope). Rule `when` conditions receive
// only role + resource. Order and spelling noise in the lists do not matter,
// so they are normalized (deduplicated, sorted) to keep one policy per
// distinct identity. Add a field here only when a rule truly depends on it.
const keyList = (list) => [...new Set(normalizeIdList(list))].sort();

export const policySignature = ({ isAuthenticated, user }) =>
  JSON.stringify([
    Boolean(isAuthenticated),
    roleOf(user),
    keyList(user?.between),
    keyList(user?.assignedBranch),
    user?.globalAccess === true,
  ]);

// What the session layer watches to drop everything loaded under an old identity (role, assignments, global access, disabled account).
// Broader than `policySignature`: a disabled account changes the session even though it changes no grant.
export const sessionAccessSignature = (user) =>
  user
    ? JSON.stringify([
        user.role,
        [...(user.between || [])].sort(),
        [...(user.assignedBranch || [])].sort(),
        user.globalAccess === true,
        user.isAvailable !== false,
      ])
    : null;

export const getPolicy = (input) => {
  const key = policySignature(input || {});
  let policy = cache.get(key);
  if (!policy) {
    if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
    policy = createPolicy(input);
    cache.set(key, policy);
  }
  return policy;
};
